import React from 'react'
import { Skeleton } from '../../../content/Skeleton/Skeleton'
import { CommonwealthGamesWidgetCta } from '../CommonwealthGamesWidgetCta/CommonwealthGamesWidgetCta'
import { useProduct } from '../../../__product'
import {
    CommonwealthGamesMedalTallySortDirection,
    CommonwealthGamesMedalTallyRow,
    CommonwealthGamesMedalTallySortKey,
    getSortedMedalTallyRows,
} from './CommonwealthGamesMedalTallyWidget.data'
import {
    StyledCountry,
    StyledCountryCell,
    StyledCountryFlag,
    StyledCountryName,
    StyledMedalHeader,
    StyledMedalHeaderLabel,
    StyledMedalValueCircle,
    StyledMedalValuePill,
    StyledMedalTallyHeadCell,
    StyledMedalTallyTable,
    StyledMedalTallyTableWrapper,
    StyledNumberCell,
    StyledRankCell,
    StyledFullMedalTallyButtonWrapper,
    StyledTotalCell,
    StyledTotalHeadCell,
    StyledSortButton,
    StyledTotalValue,
    StyledWidgetErrorText,
    StyledWidgetErrorTitle,
    StyledWidgetPlaceholder,
    StyledWidgetState,
    StyledTableRow,
} from './CommonwealthGamesMedalTallyWidget.styled'

export type CommonwealthGamesMedalTallyWidgetSize = 'default' | 'small'
export type CommonwealthGamesMedalTallyWidgetColorMode = 'light' | 'dark'

const getDefaultSortDirection = (
    sortKey: CommonwealthGamesMedalTallySortKey,
): CommonwealthGamesMedalTallySortDirection =>
    sortKey === 'country' ? 'asc' : 'desc'

const getLoadingPlaceholderHeight = ({
    size,
    maxRows,
}: {
    size: CommonwealthGamesMedalTallyWidgetSize
    maxRows?: number
}) => {
    const fallbackRows = size === 'default' ? 6 : 5
    const visibleRows =
        typeof maxRows === 'number' && maxRows > 0 ? maxRows : fallbackRows

    if (size === 'default') {
        return 60 + visibleRows * 42
    }

    return 52 + visibleRows * 36
}

export interface CommonwealthGamesMedalTallyWidgetProps {
    loading: boolean
    error: string | null
    rows: CommonwealthGamesMedalTallyRow[]
    showPreGamesState?: boolean
    showEmptyDataState?: boolean
    maxRows?: number
    fullMedalTallyUrl?: string
    colorMode?: CommonwealthGamesMedalTallyWidgetColorMode
    sortable?: boolean
    size?: CommonwealthGamesMedalTallyWidgetSize
    includeTotal?: boolean
    inline?: boolean
}

export const CommonwealthGamesMedalTallyWidget: React.FC<CommonwealthGamesMedalTallyWidgetProps> =
    ({
        loading,
        error,
        rows,
        showPreGamesState = false,
        showEmptyDataState = false,
        maxRows,
        fullMedalTallyUrl,
        colorMode = 'light',
        sortable = false,
        size = 'default',
        includeTotal,
        inline,
    }) => {
        const product = useProduct()
        const is7News = product === 'sevennews'

        const [isExpanded, setIsExpanded] = React.useState(false)
        const [sortBy, setSortBy] =
            React.useState<CommonwealthGamesMedalTallySortKey>('gold')
        const [sortDirection, setSortDirection] =
            React.useState<CommonwealthGamesMedalTallySortDirection>(
                getDefaultSortDirection('gold'),
            )

        if (loading) {
            return (
                <CommonwealthGamesMedalTallyWidgetLoadingState
                    maxRows={maxRows}
                    fullMedalTallyUrl={fullMedalTallyUrl}
                    size={size}
                    colorMode={colorMode}
                />
            )
        }

        if (error) {
            return (
                <CommonwealthGamesMedalTallyWidgetErrorState
                    error={error}
                    size={size}
                    colorMode={colorMode}
                />
            )
        }

        if (showPreGamesState) {
            return (
                <CommonwealthGamesMedalTallyWidgetPreGamesState
                    maxRows={maxRows}
                    size={size}
                    colorMode={colorMode}
                />
            )
        }

        if (showEmptyDataState) {
            return (
                <CommonwealthGamesMedalTallyWidgetEmptyDataState
                    maxRows={maxRows}
                    size={size}
                    colorMode={colorMode}
                />
            )
        }

        const sortedRows = getSortedMedalTallyRows(rows, sortBy, sortDirection)
        const shouldUseScrollableTable =
            is7News &&
            typeof maxRows === 'number' &&
            maxRows > 0 &&
            !fullMedalTallyUrl &&
            !isExpanded &&
            sortedRows.length > maxRows
        const shouldLimitRows =
            typeof maxRows === 'number' &&
            maxRows > 0 &&
            !shouldUseScrollableTable &&
            !isExpanded
        const summaryRow = sortedRows.find((r) => r.countryName === 'TOTAL')
        const displayedRows = shouldLimitRows
            ? [
                  ...sortedRows.slice(0, maxRows),
                  ...(includeTotal && summaryRow ? [summaryRow] : []),
              ]
            : sortedRows
        const shouldShowFullMedalTallyCta =
            Boolean(fullMedalTallyUrl) &&
            displayedRows.length < sortedRows.length
        const scrollMaxHeight =
            shouldUseScrollableTable && typeof maxRows === 'number'
                ? size === 'default'
                    ? 80 + maxRows * 49
                    : 58 + maxRows * 36
                : undefined

        const handleShowFullMedalTally = () => {
            if (fullMedalTallyUrl) {
                window.location.href = fullMedalTallyUrl
                return
            }

            setIsExpanded(true)
        }

        const handleSortChange = (
            nextSortBy: CommonwealthGamesMedalTallySortKey,
        ) => {
            if (!sortable) {
                return
            }

            if (sortBy === nextSortBy) {
                setSortDirection((currentDirection) =>
                    currentDirection === 'desc' ? 'asc' : 'desc',
                )
                return
            }

            setSortBy(nextSortBy)
            setSortDirection(getDefaultSortDirection(nextSortBy))
        }

        return (
            <StyledMedalTallyTableWrapper
                size={size}
                colorMode={colorMode}
                scrollMaxHeight={scrollMaxHeight}
                inline={inline}
            >
                <StyledMedalTallyTable>
                    <thead>
                        <tr>
                            <StyledMedalTallyHeadCell />
                            <StyledMedalTallyHeadCell>
                                <StyledSortButton
                                    type="button"
                                    isActive={sortBy === 'country'}
                                    aria-label="Country"
                                    align="left"
                                    disabled={!sortable}
                                    onClick={() => handleSortChange('country')}
                                >
                                    Country
                                </StyledSortButton>
                            </StyledMedalTallyHeadCell>
                            <StyledMedalTallyHeadCell>
                                <StyledSortButton
                                    type="button"
                                    isActive={sortBy === 'gold'}
                                    aria-label="Gold"
                                    disabled={!sortable}
                                    onClick={() => handleSortChange('gold')}
                                >
                                    <StyledMedalHeader>
                                        <StyledMedalValueCircle
                                            medalType="gold"
                                            aria-hidden="true"
                                        >
                                            G
                                        </StyledMedalValueCircle>
                                        <StyledMedalHeaderLabel>
                                            Gold
                                        </StyledMedalHeaderLabel>
                                    </StyledMedalHeader>
                                </StyledSortButton>
                            </StyledMedalTallyHeadCell>
                            <StyledMedalTallyHeadCell>
                                <StyledSortButton
                                    type="button"
                                    isActive={sortBy === 'silver'}
                                    aria-label="Silver"
                                    disabled={!sortable}
                                    onClick={() => handleSortChange('silver')}
                                >
                                    <StyledMedalHeader>
                                        <StyledMedalValueCircle
                                            medalType="silver"
                                            aria-hidden="true"
                                        >
                                            S
                                        </StyledMedalValueCircle>
                                        <StyledMedalHeaderLabel>
                                            Silver
                                        </StyledMedalHeaderLabel>
                                    </StyledMedalHeader>
                                </StyledSortButton>
                            </StyledMedalTallyHeadCell>
                            <StyledMedalTallyHeadCell>
                                <StyledSortButton
                                    type="button"
                                    isActive={sortBy === 'bronze'}
                                    aria-label="Bronze"
                                    disabled={!sortable}
                                    onClick={() => handleSortChange('bronze')}
                                >
                                    <StyledMedalHeader>
                                        <StyledMedalValueCircle
                                            medalType="bronze"
                                            aria-hidden="true"
                                        >
                                            B
                                        </StyledMedalValueCircle>
                                        <StyledMedalHeaderLabel>
                                            Bronze
                                        </StyledMedalHeaderLabel>
                                    </StyledMedalHeader>
                                </StyledSortButton>
                            </StyledMedalTallyHeadCell>
                            <StyledTotalHeadCell>
                                <StyledSortButton
                                    type="button"
                                    isActive={sortBy === 'total'}
                                    aria-label="Total"
                                    disabled={!sortable}
                                    onClick={() => handleSortChange('total')}
                                >
                                    Total
                                </StyledSortButton>
                            </StyledTotalHeadCell>
                        </tr>
                    </thead>
                    <tbody>
                        {displayedRows.map((row) => (
                            <StyledTableRow
                                key={row.organisationCode}
                                includeTotal={includeTotal}
                            >
                                <StyledRankCell>
                                    {row.displayRank ?? ''}
                                </StyledRankCell>
                                <StyledCountryCell>
                                    <StyledCountry>
                                        {row.flagSrc && (
                                            <StyledCountryFlag
                                                src={row.flagSrc}
                                                alt={`${row.countryName} flag`}
                                            />
                                        )}
                                        <StyledCountryName>
                                            {row.countryName}
                                        </StyledCountryName>
                                    </StyledCountry>
                                </StyledCountryCell>
                                <StyledNumberCell>
                                    <StyledMedalValuePill medalType="gold">
                                        {row.gold}
                                    </StyledMedalValuePill>
                                </StyledNumberCell>
                                <StyledNumberCell>
                                    <StyledMedalValuePill medalType="silver">
                                        {row.silver}
                                    </StyledMedalValuePill>
                                </StyledNumberCell>
                                <StyledNumberCell>
                                    <StyledMedalValuePill medalType="bronze">
                                        {row.bronze}
                                    </StyledMedalValuePill>
                                </StyledNumberCell>
                                <StyledTotalCell>
                                    <StyledTotalValue>
                                        {row.total}
                                    </StyledTotalValue>
                                </StyledTotalCell>
                            </StyledTableRow>
                        ))}
                    </tbody>
                </StyledMedalTallyTable>
                {shouldShowFullMedalTallyCta && (
                    <CommonwealthGamesMedalTallyWidgetCta
                        href={fullMedalTallyUrl}
                        colorMode={colorMode}
                    />
                )}
            </StyledMedalTallyTableWrapper>
        )
    }

CommonwealthGamesMedalTallyWidget.displayName =
    'CommonwealthGamesMedalTallyWidget'

const CommonwealthGamesMedalTallyWidgetLoadingState = ({
    maxRows,
    fullMedalTallyUrl,
    size,
    colorMode,
}: {
    maxRows?: number
    fullMedalTallyUrl?: string
    size: CommonwealthGamesMedalTallyWidgetSize
    colorMode: CommonwealthGamesMedalTallyWidgetColorMode
}) => {
    const shouldShowCta =
        typeof maxRows === 'number' && maxRows > 0 && Boolean(fullMedalTallyUrl)
    const placeholderHeight = getLoadingPlaceholderHeight({
        size,
        maxRows,
    })

    return (
        <StyledMedalTallyTableWrapper size={size} colorMode={colorMode}>
            <StyledWidgetPlaceholder placeholderHeight={placeholderHeight}>
                <Skeleton
                    height={placeholderHeight}
                    dark={colorMode === 'dark'}
                />
            </StyledWidgetPlaceholder>
            {shouldShowCta && (
                <CommonwealthGamesMedalTallyWidgetCta
                    loading
                    colorMode={colorMode}
                />
            )}
        </StyledMedalTallyTableWrapper>
    )
}

const CommonwealthGamesMedalTallyWidgetErrorState = ({
    error,
    size,
    colorMode,
}: {
    error: string
    size: CommonwealthGamesMedalTallyWidgetSize
    colorMode: CommonwealthGamesMedalTallyWidgetColorMode
}) => {
    return (
        <StyledMedalTallyTableWrapper size={size} colorMode={colorMode}>
            <StyledWidgetState>
                <StyledWidgetErrorTitle>
                    Unable to load medal tally
                </StyledWidgetErrorTitle>
                <StyledWidgetErrorText>
                    Please refresh the page or try again shortly.
                </StyledWidgetErrorText>
            </StyledWidgetState>
        </StyledMedalTallyTableWrapper>
    )
}

const CommonwealthGamesMedalTallyWidgetPreGamesState = ({
    maxRows,
    size,
    colorMode,
}: {
    maxRows?: number
    size: CommonwealthGamesMedalTallyWidgetSize
    colorMode: CommonwealthGamesMedalTallyWidgetColorMode
}) => {
    const minHeight = getLoadingPlaceholderHeight({
        size,
        maxRows,
    })

    return (
        <StyledMedalTallyTableWrapper size={size} colorMode={colorMode}>
            <StyledWidgetState minHeight={minHeight}>
                <StyledWidgetErrorTitle>
                    Games begin 23 July
                </StyledWidgetErrorTitle>
                <StyledWidgetErrorText>
                    Medal standings will appear once competition begins.
                </StyledWidgetErrorText>
            </StyledWidgetState>
        </StyledMedalTallyTableWrapper>
    )
}

const CommonwealthGamesMedalTallyWidgetEmptyDataState = ({
    maxRows,
    size,
    colorMode,
}: {
    maxRows?: number
    size: CommonwealthGamesMedalTallyWidgetSize
    colorMode: CommonwealthGamesMedalTallyWidgetColorMode
}) => {
    const minHeight = getLoadingPlaceholderHeight({
        size,
        maxRows,
    })

    return (
        <StyledMedalTallyTableWrapper size={size} colorMode={colorMode}>
            <StyledWidgetState minHeight={minHeight}>
                <StyledWidgetErrorTitle>
                    No medal data available yet
                </StyledWidgetErrorTitle>
                <StyledWidgetErrorText>
                    Medal standings will appear once competition begins.
                </StyledWidgetErrorText>
            </StyledWidgetState>
        </StyledMedalTallyTableWrapper>
    )
}

const CommonwealthGamesMedalTallyWidgetCta = ({
    loading = false,
    colorMode = 'light',
    href,
}: {
    loading?: boolean
    colorMode?: CommonwealthGamesMedalTallyWidgetColorMode
    href?: string
}) => {
    return (
        <StyledFullMedalTallyButtonWrapper>
            <CommonwealthGamesWidgetCta
                loading={loading}
                href={href}
                label="Show full medal tally"
                colorMode={colorMode}
            />
        </StyledFullMedalTallyButtonWrapper>
    )
}
